import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, TrendingUp, TrendingDown, Radio, BookOpen, ChevronRight, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../context/ThemeContext';
import { formatPrice } from '../utils/formatters';
import { useSocket } from '../context/SocketContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function MarketsPage() {
  const { colors } = useTheme();
  const { prices, simDate } = useSocket();
  const navigate = useNavigate();
  const [stocks, setStocks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [learnOpen, setLearnOpen] = useState(false);

  useEffect(() => { fetchStocks(); }, []);

  const fetchStocks = async () => {
    try {
      const response = await axios.get(`${API_URL}/stocks`);
      setStocks(response.data);
    } catch (error) {
      console.error('Failed to fetch stocks', error);
      toast.error('Failed to load stocks');
    } finally {
      setLoading(false);
    }
  };

  const simActive = !!simDate;

  const filteredStocks = stocks.filter(stock =>
    stock.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stock.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="markets-page">

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: colors.primaryText, margin: 0, letterSpacing: '-0.01em' }}>Markets</h1>
          <p style={{ fontSize: 12, color: colors.secondaryText, margin: '2px 0 0' }}>Explore stocks and discover opportunities</p>
        </div>
        {simActive && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 20,
            background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)', color: '#8b5cf6',
          }}>
            <Radio style={{ width: 10, height: 10, animation: 'pulse 1.5s infinite' }} />
            LIVE-S
          </span>
        )}
      </div>

      {/* Learning nudge */}
      <div style={{
        borderRadius: 14, overflow: 'hidden',
        background: learnOpen ? 'rgba(255,255,255,0.03)' : 'linear-gradient(135deg, #6366f118 0%, #6366f108 100%)',
        border: learnOpen ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(99,102,241,0.3)',
        transition: 'background 0.2s, border-color 0.2s',
      }}>
        <button onClick={() => setLearnOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', cursor: 'pointer', background: 'none', border: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 9, background: learnOpen ? 'rgba(255,255,255,0.06)' : 'rgba(99,102,241,0.18)', border: `1px solid ${learnOpen ? 'rgba(255,255,255,0.1)' : 'rgba(99,102,241,0.3)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <BookOpen style={{ width: 13, height: 13, color: learnOpen ? 'rgba(255,255,255,0.4)' : '#818cf8' }} />
            </div>
            <p style={{ fontSize: 12, fontWeight: 700, color: learnOpen ? 'rgba(255,255,255,0.4)' : '#a5b4fc', margin: 0, letterSpacing: '0.03em' }}>Read charts better</p>
          </div>
          <ChevronDown style={{ width: 14, height: 14, color: learnOpen ? 'rgba(255,255,255,0.25)' : '#6366f199', transform: learnOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
        </button>
        {learnOpen && (
          <div style={{ padding: '0 10px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              { level: 6, color: '#6366f1', label: 'Reading Charts Like a Story', sub: 'How to interpret price action and trends' },
              { level: 8, color: '#4ecca3', label: 'Chart Patterns', sub: 'Recognise breakouts, reversals and continuations' },
            ].map(({ level, color, label, sub }) => (
              <button key={level} onClick={() => navigate(`/learning?view=level&level=${level}&card=0`)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderRadius: 11, textAlign: 'left', cursor: 'pointer', background: `${color}0d`, border: `1px solid ${color}22` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <BookOpen style={{ width: 13, height: 13, flexShrink: 0, color }} />
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color, margin: 0, lineHeight: 1.3 }}>{label}</p>
                    <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', margin: '2px 0 0' }}>{sub}</p>
                  </div>
                </div>
                <ChevronRight style={{ width: 12, height: 12, flexShrink: 0, color: `${color}50` }} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Search */}
      <div style={{ position: 'relative' }}>
        <Search style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, color: colors.secondaryText }} />
        <input
          type="text"
          placeholder="Search stocks..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%', paddingLeft: 38, paddingRight: 14, paddingTop: 10, paddingBottom: 10,
            borderRadius: 12, fontSize: 13, outline: 'none', boxSizing: 'border-box',
            background: `linear-gradient(135deg, ${colors.accent}10 0%, ${colors.accent}04 100%)`,
            border: `1px solid ${colors.border}`, color: colors.primaryText,
          }}
          data-testid="stock-search-input"
        />
      </div>

      {/* Stock list */}
      <div style={{ borderRadius: 16, overflow: 'hidden', background: `linear-gradient(135deg, ${colors.accent}0a 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.border}` }}>

        {/* Mobile list */}
        <div className="md:hidden">
          {loading ? (
            <div style={{ padding: '24px', textAlign: 'center', color: colors.secondaryText, fontSize: 13 }}>Loading stocks...</div>
          ) : filteredStocks.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: colors.secondaryText, fontSize: 13 }}>No stocks found</div>
          ) : (
            filteredStocks.map((stock, i) => {
              const cleanSym = stock.symbol.replace('.NS', '');
              const live = prices[cleanSym];
              const displayPrice = live?.price ?? stock.current_price;
              const displayChangePct = live?.change_pct ?? stock.change_pct;
              const isUp = displayChangePct >= 0;
              const chgColor = isUp ? colors.profit : colors.loss;

              return (
                <button
                  key={stock.symbol}
                  onClick={() => navigate(`/stock/${cleanSym}`)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer',
                    borderBottom: i < filteredStocks.length - 1 ? `1px solid ${colors.border}` : 'none',
                  }}
                  data-testid={`stock-card-${stock.symbol}`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    {/* Symbol avatar */}
                    <div style={{
                      width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                      background: `linear-gradient(135deg, ${live ? '#8b5cf620' : `${colors.accent}14`}, ${live ? '#8b5cf608' : `${colors.accent}06`})`,
                      border: `1px solid ${live ? '#8b5cf630' : `${colors.accent}20`}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{ fontSize: 9, fontWeight: 800, color: live ? '#8b5cf6' : colors.secondaryText, letterSpacing: '0.02em' }}>
                        {cleanSym.slice(0, 3)}
                      </span>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: colors.primaryText, margin: 0 }}>{cleanSym}</p>
                      <p style={{ fontSize: 10, color: colors.secondaryText, margin: '1px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>{stock.company_name}</p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: live ? '#8b5cf6' : colors.primaryText, margin: 0, fontVariantNumeric: 'tabular-nums' }}>
                      {formatPrice(displayPrice)}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, marginTop: 2 }}>
                      {isUp ? <TrendingUp style={{ width: 10, height: 10, color: chgColor }} /> : <TrendingDown style={{ width: 10, height: 10, color: chgColor }} />}
                      <span style={{ fontSize: 11, fontWeight: 600, color: chgColor, fontVariantNumeric: 'tabular-nums' }}>
                        {displayChangePct >= 0 ? '+' : ''}{displayChangePct?.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: colors.inputBg, borderBottom: `1px solid ${colors.border}` }}>
              <tr>
                {['Symbol', 'Company', 'Sector', 'Price', 'Change'].map((h, i) => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: i >= 3 ? 'right' : 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: colors.secondaryText }}>
                    {h} {h === 'Price' && simActive && <span style={{ color: '#8b5cf6' }}>●</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" style={{ padding: '24px', textAlign: 'center', color: colors.secondaryText, fontSize: 13 }}>Loading stocks...</td></tr>
              ) : filteredStocks.length === 0 ? (
                <tr><td colSpan="5" style={{ padding: '24px', textAlign: 'center', color: colors.secondaryText, fontSize: 13 }}>No stocks found</td></tr>
              ) : (
                filteredStocks.map((stock) => {
                  const cleanSym = stock.symbol.replace('.NS', '');
                  const live = prices[cleanSym];
                  const displayPrice = live?.price ?? stock.current_price;
                  const displayChangePct = live?.change_pct ?? stock.change_pct;
                  const isUp = displayChangePct >= 0;
                  const chgColor = isUp ? colors.profit : colors.loss;

                  return (
                    <tr key={stock.symbol} onClick={() => navigate(`/stock/${cleanSym}`)}
                      style={{ borderBottom: `1px solid ${colors.border}`, cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.inputBg}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                      data-testid={`stock-row-${stock.symbol}`}>
                      <td style={{ padding: '8px 12px', fontWeight: 700, fontSize: 13, color: colors.primaryText }}>{cleanSym}</td>
                      <td style={{ padding: '8px 12px', fontSize: 12, color: colors.secondaryText }}>{stock.company_name}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: `${colors.accent}12`, color: colors.secondaryText, border: `1px solid ${colors.accent}18` }}>{stock.sector}</span>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: live ? '#8b5cf6' : colors.primaryText, fontVariantNumeric: 'tabular-nums' }}>{formatPrice(displayPrice)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, color: chgColor }}>
                          {isUp ? <TrendingUp style={{ width: 12, height: 12 }} /> : <TrendingDown style={{ width: 12, height: 12 }} />}
                          <span style={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{isUp ? '+' : ''}{displayChangePct?.toFixed(2)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default MarketsPage;
